import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAuth } from "@/auth/RequireAuth";
import { ChangePasswordPage } from "@/pages/admin/ChangePasswordPage";
import { LoginPage } from "@/pages/admin/LoginPage";
import { PromptSettingsPage } from "@/pages/admin/PromptSettingsPage";
import { RequireTab } from "@/pages/admin/RequireTab";
import { TabFormPage } from "@/pages/admin/TabFormPage";
import { TabKnowledgePage } from "@/pages/admin/TabKnowledgePage";
import { TabSyncPage } from "@/pages/admin/TabSyncPage";
import { TabsPage } from "@/pages/admin/TabsPage";
import { TabsProvider } from "@/pages/admin/TabsContext";
import { TestTicketDetailPage } from "@/pages/admin/TestTicketDetailPage";
import { TestTicketsListPage } from "@/pages/admin/TestTicketsListPage";
import { TicketDetailPage } from "@/pages/admin/TicketDetailPage";
import { TicketsListPage } from "@/pages/admin/TicketsListPage";
import { UsersPage } from "@/pages/admin/UsersPage";
import { SubmitTicketPage } from "@/pages/student/SubmitTicketPage";

/**
 * TabsProvider must wrap every route that touches tab data (which is all admin
 * pages except login/change-password). It's mounted inside the auth gate so
 * unauthenticated visitors never fire the /api/admin/tabs request.
 */
function AdminGate({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <TabsProvider>{children}</TabsProvider>
    </RequireAuth>
  );
}

function AdminSuperGate({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth role="super_admin">
      <TabsProvider>{children}</TabsProvider>
    </RequireAuth>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route path="/submit" element={<SubmitTicketPage />} />
      <Route path="/admin/login" element={<LoginPage />} />

      {/* Ticket queue (regular admins + super) */}
      <Route
        path="/admin"
        element={
          <AdminGate>
            <RequireTab>
              <TicketsListPage />
            </RequireTab>
          </AdminGate>
        }
      />
      <Route
        path="/admin/tickets/:id"
        element={
          <AdminGate>
            <RequireTab>
              <TicketDetailPage />
            </RequireTab>
          </AdminGate>
        }
      />

      {/* User management (super only) */}
      <Route
        path="/admin/users"
        element={
          <AdminSuperGate>
            <UsersPage />
          </AdminSuperGate>
        }
      />

      {/* AI prompt settings (super only) */}
      <Route
        path="/admin/settings/prompt"
        element={
          <AdminSuperGate>
            <PromptSettingsPage />
          </AdminSuperGate>
        }
      />

      {/* Tab management (super only) */}
      <Route
        path="/admin/tabs"
        element={
          <AdminSuperGate>
            <TabsPage />
          </AdminSuperGate>
        }
      />
      <Route
        path="/admin/tabs/new"
        element={
          <AdminSuperGate>
            <TabFormPage />
          </AdminSuperGate>
        }
      />
      <Route
        path="/admin/tabs/:id/edit"
        element={
          <AdminSuperGate>
            <TabFormPage />
          </AdminSuperGate>
        }
      />
      <Route
        path="/admin/tabs/:id/knowledge"
        element={
          <AdminSuperGate>
            <TabKnowledgePage />
          </AdminSuperGate>
        }
      />
      <Route
        path="/admin/tabs/:id/sync"
        element={
          <AdminSuperGate>
            <TabSyncPage />
          </AdminSuperGate>
        }
      />

      {/* Test tickets (any admin with tab access; superadmin can create/delete) */}
      <Route
        path="/admin/tabs/:tabId/test-tickets"
        element={
          <AdminGate>
            <RequireTab>
              <TestTicketsListPage />
            </RequireTab>
          </AdminGate>
        }
      />
      <Route
        path="/admin/tabs/:tabId/test-tickets/:id"
        element={
          <AdminGate>
            <RequireTab>
              <TestTicketDetailPage />
            </RequireTab>
          </AdminGate>
        }
      />

      <Route
        path="/admin/change-password"
        element={
          <RequireAuth allowDuringPasswordChange>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="text-center">
        <p className="text-4xl font-bold mb-2">404</p>
        <p className="text-slate-500 dark:text-slate-400">Page not found.</p>
      </div>
    </div>
  );
}
